import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Typography, Tag, Space, Card, Avatar, Statistic, Row, Col, Divider } from 'antd';
import { usePostContext } from '../../App';
import './index.css';
import 'antd/dist/reset.css';

const { Title: AntTitle, Text, Paragraph } = Typography;

const Home = () => {
  const { posts, loggedInUser } = usePostContext();
  const [userInfo, setUserInfo] = useState(null);

  // 从localStorage读取用户信息
  const getUserInfoFromStorage = (username) => {
    const savedUserInfos = localStorage.getItem('userProfiles');
    if (!savedUserInfos) return null;
    const userProfiles = JSON.parse(savedUserInfos);
    return userProfiles[username] || null;
  };

  // 登录状态变化时加载用户信息
  useEffect(() => {
    if (loggedInUser) {
      const savedInfo = getUserInfoFromStorage(loggedInUser.username);
      setUserInfo(savedInfo);
    } else {
      setUserInfo(null);
    }
  }, [loggedInUser]);

  // 1. 筛选最新文章（取前6篇，按发布时间倒序）
  const latestPosts = [...posts]
    .sort((a, b) => new Date(b.publishTime) - new Date(a.publishTime))
    .slice(0, 6);

  // 2. 提取所有标签（去重）
  const allTags = Array.from(new Set(posts.flatMap(post => 
    Array.isArray(post.tags) ? post.tags : [post.tags]
  )));

  // 3. 计算当前用户统计数据
  const userPosts = posts.filter(post => post.author === loggedInUser?.username);
  const userStats = {
    totalPosts: userPosts.length,
    totalViews: userPosts.reduce((sum, post) => sum + (post.views || 0), 0),
    totalLikes: userPosts.reduce((sum, post) => sum + (post.likeCount || 0), 0)
  };

  // 4. 获取推荐文章（点赞数最多的3篇）
  const recommendedPosts = [...posts]
    .sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))
    .slice(0, 3);

  return (
    <div className="home-container">
      {/* 1. Banner 区域 */}
      <div className="home-banner">
        <div className="home-banner-content">
          <AntTitle level={2} className="home-banner-title">我的个人博客</AntTitle>
          <Paragraph className="home-banner-desc">记录生活、分享知识与技术的小天地</Paragraph>
          {loggedInUser ? (
            <Link to="/write">
              <Button type="primary" size="large" className="home-banner-btn">开始创作</Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button type="primary" size="large" className="home-banner-btn">登录后创作</Button>
            </Link>
          )}
        </div>
      </div>

      {/* 2. 统计信息卡片 - 仅登录用户显示 */}
      {loggedInUser && (
        <div className="home-stats-section">
          <Row gutter={16} justify="center" className="home-stats-row">
            <Col span={8}>
              <Card className="home-stat-card">
                <Statistic 
                  title="我的文章数" 
                  value={userStats.totalPosts} 
                  prefix={<span className="home-stat-icon">📝</span>}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card className="home-stat-card">
                <Statistic 
                  title="我的访问量" 
                  value={userStats.totalViews} 
                  prefix={<span className="home-stat-icon">👁️</span>}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card className="home-stat-card">
                <Statistic 
                  title="我的点赞数" 
                  value={userStats.totalLikes} 
                  prefix={<span className="home-stat-icon">👍</span>}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* 3. 推荐文章区域 - 所有用户可见 */}
      {recommendedPosts.length > 0 && (
        <div className="home-recommended-section">
          <AntTitle level={3} className="home-section-title">热门推荐</AntTitle>
          <div className="home-recommended-list">
            {recommendedPosts.map(post => (
              <Card key={post.id} className="home-recommended-card" hoverable>
                <div className="home-recommended-content">
                  <div className="home-recommended-text">
                    <AntTitle level={4} className="home-recommended-title">
                      <Link to={`/postdetail/${post.id}`}>{post.title}</Link>
                    </AntTitle>
                    <Paragraph className="home-recommended-summary" ellipsis={{ rows: 2 }}>
                      {post.summary || '暂无摘要'}
                    </Paragraph>
                    <div className="home-recommended-meta">
                      <Text type="secondary">
                        {post.publishTime} · 作者：{post.author} · 👁️{post.views || 0} · 👍{post.likeCount || 0}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* 4. 核心内容区（最新文章 + 侧边栏） */}
      <div className="home-content">
        {/* 左侧：最新文章列表 */}
        <div className="home-main">
          <AntTitle level={3} className="home-section-title">最新发布</AntTitle>
          {!loggedInUser ? (
            // 未登录：隐藏文章，提示登录
            <div className="home-not-login">
              <Empty 
                description={<Text type="secondary">请登录后查看最新文章</Text>}
                style={{ margin: '50px 0' }}
              />
              <Link to="/login">
                <Button type="primary" className="home-login-btn">立即登录</Button>
              </Link>
            </div>
          ) : latestPosts.length === 0 ? (
            // 已登录但无文章：提示发布第一篇
            <div className="home-no-posts">
              <Empty description="暂无文章，快来发布你的第一篇吧～" />
              <Link to="/write">
                <Button type="primary" className="home-write-btn">发布文章</Button>
              </Link>
            </div>
          ) : (
            // 已登录且有文章：显示最新文章卡片
            <div className="home-post-list">
              {latestPosts.map(post => (
                <Card key={post.id} className="home-post-card" hoverable>
                  <div className="home-post-header">
                    <AntTitle level={4} className="home-post-title">
                      <Link to={`/postdetail/${post.id}`}>{post.title}</Link>
                    </AntTitle>
                    <Text type="secondary" className="home-post-meta">
                      {post.publishTime} · 作者：{post.author} · 👁️{post.views || 0} · 👍{post.likeCount || 0}
                    </Text>
                  </div>
                  <Paragraph className="home-post-summary" ellipsis={{ rows: 2 }}>
                    {post.summary || '暂无摘要，点击查看全文...'}
                  </Paragraph>
                  <div className="home-post-footer">
                    <Space size="small" className="home-post-tags">
                      {Array.isArray(post.tags) 
                        ? post.tags.map(tag => <Tag key={tag} size="small">{tag}</Tag>) 
                        : <Tag size="small">{post.tags}</Tag>
                      }
                    </Space>
                    <Link to={`/postdetail/${post.id}`} className="home-post-link">
                      阅读全文 →
                    </Link>
                  </div>
                </Card>
              ))}
              <div className="home-view-all">
                <Link to="/archive">
                  <Button type="default" size="middle">查看全部文章</Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* 右侧：侧边栏 */}
        <div className="home-sidebar">
          {/* 标签云 */}
          <div className="home-sidebar-card">
            <AntTitle level={4} className="home-sidebar-title">标签云</AntTitle>
            {allTags.length === 0 ? (
              <Text type="secondary" className="home-no-tags">暂无标签</Text>
            ) : (
              <div className="home-tag-cloud">
                {allTags.map(tag => (
                  <Link to="/archive" key={tag} className="home-tag-link">
                    <Tag size="middle">{tag}</Tag>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* 个人简介 - 链接到 About 页面 */}
          <div className="home-sidebar-card">
            <div className="home-sidebar-header">
              <AntTitle level={4} className="home-sidebar-title">关于我</AntTitle>
              {loggedInUser && (
                <Link to="/my" className="home-about-link">
                  <Button type="link" size="small">编辑</Button>
                </Link>
              )}
            </div>
            
            <Link to="/my" className="home-profile-link">
              <div className="home-profile">
                <Avatar 
                  size={64} 
                  src={userInfo?.avatarUrl || loggedInUser?.avatar || 'https://joeschmoe.io/api/v1/random'} 
                  className="home-avatar"
                />
                <div className="home-profile-info">
                  <Text className="home-username">
                    {userInfo?.name || loggedInUser?.username || '游客'}
                  </Text>
                  <Paragraph className="home-profile-desc" ellipsis={{ rows: 2 }}>
                    {userInfo?.intro || 
                     (loggedInUser ? '热爱分享的博客创作者，记录生活中的点滴与成长～' : '登录后可查看完整个人资料')}
                  </Paragraph>
                </div>
              </div>
            </Link>
            
            {loggedInUser && (
              <div className="home-profile-stats">
                <div className="home-profile-stat">
                  <span className="home-stat-number">{userStats.totalPosts}</span>
                  <span className="home-stat-label">文章</span>
                </div>
                <div className="home-profile-stat">
                  <span className="home-stat-number">{userStats.totalViews}</span>
                  <span className="home-stat-label">访问</span>
                </div>
                <div className="home-profile-stat">
                  <span className="home-stat-number">{userStats.totalLikes}</span>
                  <span className="home-stat-label">点赞</span>
                </div>
              </div>
            )}
          </div>

          {/* 最近活跃用户 */}
          <div className="home-sidebar-card">
            <AntTitle level={4} className="home-sidebar-title">活跃作者</AntTitle>
            <div className="home-active-users">
              {Array.from(new Set(posts.map(post => post.author))).slice(0, 5).map((author, index) => (
                <div key={author} className="home-user-item">
                  <div className="home-user-rank">#{index + 1}</div>
                  <Avatar 
                    size={32} 
                    src={`https://joeschmoe.io/api/v1/${author}`}
                    className="home-user-avatar"
                  />
                  <span className="home-user-name">{author}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 5. 底部推荐区域 */}
      <div className="home-footer-section">
        <Divider />
        <AntTitle level={3} className="home-section-title">热门话题</AntTitle>
        <div className="home-trending-topics">
          {allTags.slice(0, 5).map(tag => (
            <Link to="/archive" key={tag} className="home-topic-link">
              <Tag color="blue" className="home-topic-tag">
                #{tag}
              </Tag>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;